import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import { usePilotTraining } from "../context/PilotTrainingContext";
import GuidedTipsSettings from "../components/Help/GuidedTipsSettings";
import { trainingProgress } from "../components/Help/pilotTrainingSteps";
import {
  HELP_CONTENT_VERSION,
  answerQuestion,
  findTopic,
  searchTopics,
  topicsForRole,
} from "../components/Help/helpTopics";

// UX-HELP-CENTER-001 — searchable help over approved, versioned content.
// Read-only: no network calls. Answers are quoted from helpTopics.js and the
// page says so when nothing matches instead of inventing behavior.

function TopicLink({ id, children }) {
  return (
    <Link to={`/help?topic=${encodeURIComponent(id)}`} className="text-blue-600 hover:underline">
      {children}
    </Link>
  );
}

function TopicView({ topic, role }) {
  const related = topic.related.map((id) => findTopic(id, role)).filter(Boolean);
  return (
    <article className="rounded-lg border border-gray-200 bg-white p-5" data-topic={topic.id}>
      <h2 className="text-xl font-semibold text-gray-900 mb-1">{topic.title}</h2>
      <p className="text-sm text-gray-600 mb-4">{topic.summary}</p>
      {topic.sections.map((s) => (
        <section key={s.heading} className="mb-3">
          <h3 className="text-sm font-semibold text-gray-800">{s.heading}</h3>
          <p className="text-sm text-gray-600">{s.text}</p>
        </section>
      ))}
      {related.length > 0 && (
        <p className="text-sm text-gray-500 mt-4">
          נושאים קשורים:{" "}
          {related.map((r, i) => (
            <span key={r.id}>
              {i > 0 && " · "}
              <TopicLink id={r.id}>{r.title}</TopicLink>
            </span>
          ))}
        </p>
      )}
      <p className="text-xs text-gray-400 mt-4">
        מקור: {topic.source} · עודכן {topic.updated}
      </p>
    </article>
  );
}

function AnswerView({ answer }) {
  if (!answer.answered) {
    return (
      <div role="status" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        לא נמצא מידע מאושר שעונה על השאלה הזו, ולכן אני לא מנחש. נסו לחפש בנושאים שלמטה
        או לנסח את השאלה במילים אחרות. אם הנושא חסר בעזרה, כדאי לדווח לצוות המערכת.
      </div>
    );
  }
  return (
    <div role="status" className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-gray-800">
      <p className="font-medium mb-1">{answer.summary}</p>
      {answer.detail && (
        <p className="text-gray-700">
          <span className="font-medium">{answer.detail.heading}: </span>
          {answer.detail.text}
        </p>
      )}
      <p className="text-xs text-gray-500 mt-3">
        התשובה מצוטטת ממסמכי העזרה המאושרים (גרסה {answer.version}). מקורות:{" "}
        {answer.sources.map((s, i) => (
          <span key={s.id}>
            {i > 0 && " · "}
            <TopicLink id={s.id}>{s.title}</TopicLink>
          </span>
        ))}
      </p>
    </div>
  );
}

/**
 * PILOT-TECHNICIAN-TRAINING-001 — the relaunch point §14B requires. Training is
 * offered once on first sign-in; this is where a technician returns to it after
 * pausing or dismissing. Renders nothing outside a PilotTrainingProvider so the
 * Help Center keeps working standalone.
 */
function PilotTrainingRelaunch() {
  const training = usePilotTraining();
  const { user } = useAuth() || {};
  const { can: canAgent } = useAgentPermissions() || {};
  if (!training) return null;

  const role = user?.role;
  const isAdmin = role === "admin" || role === "owner";
  const { completedSteps, total, allStepsDone } = trainingProgress(training.progress, {
    isAdmin,
    canAgent,
    role,
  });

  const stateLabel =
    training.status === "paused"
      ? "מושהית"
      : training.status === "dismissed"
      ? "נסגרה — אפשר לפתוח מחדש"
      : allStepsDone
      ? "הושלמה"
      : "בתהליך";

  return (
    <section
      aria-label="הדרכת טכנאי פיילוט"
      data-testid="pilot-training-relaunch"
      className="rounded-lg border border-purple-200 bg-purple-50/50 p-4 mb-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">הדרכת טכנאי פיילוט</h2>
          <p className="text-xs text-gray-600 mt-1">
            מסלול העבודה המלא, צעד אחר צעד, כולל איך מזהים מצבי אמון בתוצאות. מצב: {stateLabel} ·
            הושלמו {completedSteps} מתוך {total}
          </p>
        </div>
        <button
          type="button"
          onClick={training.openTraining}
          className="shrink-0 rounded bg-purple-600 px-3 py-1.5 text-sm text-white hover:bg-purple-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400"
        >
          {training.status === "new" ? "התחל הדרכה" : "פתח הדרכה"}
        </button>
      </div>
    </section>
  );
}

export default function HelpCenterPage() {
  const { user } = useAuth() || {};
  const role = user?.role;
  const [params] = useSearchParams();
  const [query, setQuery] = useState("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState(null);

  const requestedId = params.get("topic");
  const selected = requestedId ? findTopic(requestedId, role) : null;
  const unknownRequested = Boolean(requestedId) && !selected;

  const visible = useMemo(() => {
    if (!query.trim()) return topicsForRole(role);
    return searchTopics(query, role).map((r) => r.topic);
  }, [query, role]);

  function ask(e) {
    e.preventDefault();
    if (!question.trim()) return;
    setAnswer(answerQuestion(question, role));
  }

  return (
    <div dir="rtl" className="max-w-5xl mx-auto p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">מרכז עזרה</h1>
      <p className="text-sm text-gray-500 mb-6">
        תיעוד מאושר על אופן פעולת המערכת · גרסת תוכן {HELP_CONTENT_VERSION}
      </p>

      <form onSubmit={ask} className="mb-6" aria-label="שאל את המערכת">
        <label htmlFor="help-ask" className="block text-sm font-medium text-gray-800 mb-1">
          שאלו איך המערכת עובדת
        </label>
        <div className="flex gap-2">
          <input
            id="help-ask"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="למשל: מי מאשר פעולה של סוכן AI?"
            className="flex-1 rounded border border-gray-300 px-3 py-2 text-sm"
          />
          <button
            type="submit"
            className="rounded bg-gray-800 text-white text-sm px-4 py-2 hover:bg-gray-700"
          >
            שאל
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          התשובות מבוססות על מסמכי העזרה בלבד. השאלה אינה נשלחת לשום שירות חיצוני ואינה נשמרת.
        </p>
      </form>

      {answer && (
        <div className="mb-6">
          <AnswerView answer={answer} />
        </div>
      )}

      {unknownRequested && (
        <div role="status" className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 mb-6">
          הנושא המבוקש לא נמצא או שאינו זמין עבורך.
        </div>
      )}

      {selected && (
        <div className="mb-6">
          <TopicView topic={selected} role={role} />
        </div>
      )}

      <PilotTrainingRelaunch />

      <GuidedTipsSettings />

      <section aria-label="נושאי עזרה">
        <div className="flex items-center gap-3 mb-3">
          <h2 className="text-lg font-semibold text-gray-800">נושאי עזרה</h2>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="חיפוש בנושאי עזרה"
            placeholder="חיפוש…"
            className="rounded border border-gray-300 px-3 py-1.5 text-sm"
          />
        </div>
        {visible.length === 0 ? (
          <p className="text-sm text-gray-500">לא נמצאו נושאים תואמים.</p>
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2">
            {visible.map((t) => (
              <li key={t.id} className="rounded-lg border border-gray-200 bg-white p-3">
                <p className="font-medium text-sm">
                  <TopicLink id={t.id}>{t.title}</TopicLink>
                </p>
                <p className="text-xs text-gray-600 mt-1">{t.summary}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
